import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { useNavigation } from '../../Context/NavigationContext'
import { useAuth } from '../../Context/AuthContext'
import { useIntake } from '../../Context/IntakeContext'
import { useFollowup } from '../../Context/FollowupContext'
import { ChevronRight, Bell, LogOut, X, MessageSquare, Users } from 'react-feather'
import Logo2 from '../../Assets/new-logo.png'
import ProfileModal from '../Modals/ProfileModal'

// Tooltip Portal Component
const TooltipPortal: React.FC<{ children: React.ReactNode; targetRef: React.RefObject<HTMLDivElement>; isVisible: boolean }> = ({ children, targetRef, isVisible }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (targetRef.current && isVisible) {
      const rect = targetRef.current.getBoundingClientRect()
      setPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2 + 40
      })
    }
  }, [targetRef, isVisible])

  if (!isVisible) return null

  return createPortal(
    <div
      className="fixed z-[9999] -translate-x-1/2 pointer-events-none"
      style={{ top: position.top, left: position.left }}
    >
      {children}
    </div>,
    document.body
  )
}

const SidebarComponent = () => {
  const { sideNavigation, currentSidebarTab, currentSidebarSubTab, sideNavigationClaims, sideNavigationFollowup,
    handleUpdateCurrentSidebarTab, handleUpdateCurrentSidebarType, handleUpdateCurrentSidebarSubTab,
    isMobileSidebarOpen, isMobile, closeMobileSidebar, isSidebarPinned, setSidebarPinned } = useNavigation()
  const { currentProfile } = useAuth()
  const { fetchYellowStripeRemindersData, yellowStripeRemindersData, setShowBellReminder, showBellReminder } = useIntake()
  const { showReminder, setShowReminder } = useFollowup()
  const navigate = useNavigate()
  const location = useLocation()

  const [isExpanded, setIsExpanded] = useState(false)
  const [maxHeight, setMaxHeight] = useState(window.innerHeight)
  const [expandedSubMenu, setExpandedSubMenu] = useState<string | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [showBadgeTooltip, setShowBadgeTooltip] = useState(false)
  const badgeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateDimensions = () => {
      setMaxHeight(window.innerHeight)
    }
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    fetchYellowStripeRemindersData()
  }, [])

  // Set expanded submenu based on current tab - only if the tab has subTabs
  useEffect(() => {
    if (currentSidebarTab) {
      const tabs = getNavigationTabs()
      const activeTab = tabs.find((t: any) => t.label === currentSidebarTab)
      if (activeTab?.subTabs && activeTab.subTabs.length > 0) {
        setExpandedSubMenu(currentSidebarTab)
      }
    }
  }, [currentSidebarTab, currentProfile.department])

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      closeMobileSidebar()
    }
  }, [location.pathname])

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileSidebarOpen])

  const getIconComponent = (icon: string) => {
    try {
      return require(`react-feather/dist/icons/${icon}`).default
    } catch (error) {
      return null
    }
  }

  const getNavigationTabs = () => {
    if (currentProfile.department === 'claims') return sideNavigationClaims
    if (currentProfile.department === 'followup') return sideNavigationFollowup
    return sideNavigation
  }

  const hasAccess = (tab: any) => {
    const hasPrivilegeAccess = tab.privileges.includes(currentProfile.privileges || '') ||
      (currentProfile.department === 'bd' && currentProfile.privileges === 'staff' && tab.privileges.includes('manager'))
    const hasDepartmentAccess = tab.department.includes(currentProfile.department || '')
    return hasPrivilegeAccess && hasDepartmentAccess
  }

  const handleTabClick = (tab: any) => {
    handleUpdateCurrentSidebarTab(tab.label, location.pathname)
    handleUpdateCurrentSidebarType(tab.page)

    if (tab.subTabs && tab.subTabs.length > 0) {
      // Always expand submenu for tabs with subTabs (don't toggle)
      setExpandedSubMenu(tab.label)
      if (tab.route) {
        navigate(tab.route)
      }
    } else if (tab.route) {
      navigate(tab.route)
    }
  }

  const handleSubTabClick = (tab: any, subTab: any) => {
    handleUpdateCurrentSidebarTab(tab.label, location.pathname)
    handleUpdateCurrentSidebarSubTab(subTab.label)
    navigate(subTab.route)
  }

  const handleBellClick = () => {
    setShowBellReminder(prev => ({ ...prev, isVisible: !prev.isVisible }))
    setShowReminder(false)
  }

  const getInitials = (name: string | null) => {
    if (!name) return 'U'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const tabs = getNavigationTabs().filter(hasAccess)
  const hasYellowStripeReminders = yellowStripeRemindersData && yellowStripeRemindersData.length > 0

  // Sidebar content (shared between desktop and mobile)
  const renderSidebarContent = (isForMobile: boolean = false) => (
    <div className="flex flex-col h-full">
      {/* Logo Section with Anniversary Badge */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-dark-border">
        <div className={`flex items-center gap-3 transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'justify-start w-full' : 'justify-center'}`}>
          <img
            src={Logo2}
            alt="Logo"
            className={`transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'h-8' : 'h-8 w-8 object-contain'}`}
          />
          {/* 2 Year Anniversary Badge */}
          {new Date() < new Date('2027-02-01') && (
            <div
              ref={badgeRef}
              className={`relative transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100 scale-100' : 'opacity-0 scale-0 w-0'}`}
              onMouseEnter={() => setShowBadgeTooltip(true)}
              onMouseLeave={() => setShowBadgeTooltip(false)}
            >
              <div className={`p-[1.5px] rounded-lg transition-all duration-300 cursor-pointer ${showBadgeTooltip ? 'bg-gradient-to-r from-accent-purple via-accent-pink to-accent-gold scale-110' : 'bg-dark-border'}`}>
                <div className={`w-10 h-10 rounded-[6px] flex flex-col items-center justify-center transition-all duration-300 ${showBadgeTooltip ? 'bg-dark-card' : 'bg-gradient-to-br from-accent-gold to-accent-orange'}`}>
                  <span className={`text-lg font-black leading-none transition-all duration-300 ${showBadgeTooltip ? 'bg-gradient-to-r from-accent-purple via-accent-pink to-accent-gold bg-clip-text text-transparent' : 'text-white'}`}>2</span>
                  <p className="text-[5px] font-bold text-white/90 uppercase tracking-tight leading-none">Years</p>
                </div>
              </div>
              {/* Tooltip via Portal - only for desktop */}
              {!isForMobile && (
                <TooltipPortal targetRef={badgeRef} isVisible={showBadgeTooltip && (isExpanded || isSidebarPinned)}>
                  <div className="w-48 animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-[1px] rounded-xl bg-gradient-to-r from-accent-purple via-accent-pink to-accent-gold shadow-2xl">
                      <div className="bg-dark-card rounded-[11px] px-3 py-2">
                        <p className="text-[10px] text-gray-300 text-center leading-relaxed">
                          Celebrating <span className="text-accent-gold font-semibold">2 years</span> of Intellasure. Thank you for guiding many lives towards recovery.
                        </p>
                      </div>
                    </div>
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-accent-purple rotate-45" />
                  </div>
                </TooltipPortal>
              )}
            </div>
          )}
        </div>
        {/* Close button for mobile */}
        {isForMobile && (
          <button
            onClick={closeMobileSidebar}
            className="p-2 rounded-lg hover:bg-dark-card-hover transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        )}
      </div>

      {/* Navigation Items */}
      <div className="flex-1 py-4 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-dark-border scrollbar-track-transparent">
        {/* Section Header */}
        <div className={`px-4 mb-3 transition-opacity duration-200 ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100' : 'opacity-0'}`}>
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Navigation</span>
        </div>

        {/* Navigation Tabs */}
        <div className="space-y-1 px-2">
          {tabs.map((tab) => {
            const IconComponent = getIconComponent(tab.icon)
            const isActive = currentSidebarTab === tab.label
            const hasSubTabs = tab.subTabs && tab.subTabs.length > 0
            const isSubMenuExpanded = expandedSubMenu === tab.label

            return (
              <div key={tab.label}>
                {/* Main Tab */}
                <div
                  onClick={() => handleTabClick(tab)}
                  className={`
                    group relative flex items-center gap-3 px-3 py-2.5 md:py-2.5 rounded-xl cursor-pointer
                    transition-all duration-200 ease-in-out min-h-[44px] md:min-h-0
                    ${isActive
                      ? 'bg-gradient-to-r from-accent-teal/20 to-accent-purple/10 text-accent-teal'
                      : 'text-gray-400 hover:bg-dark-card-hover hover:text-white active:bg-dark-card-hover'
                    }
                  `}
                >
                  {/* Active Indicator */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-teal rounded-r-full" />
                  )}

                  {/* Icon */}
                  <div className={`
                    flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg
                    transition-all duration-200
                    ${isActive ? 'bg-accent-teal/20' : 'group-hover:bg-dark-border/50'}
                  `}>
                    {IconComponent && (
                      <IconComponent
                        size={18}
                        className={`transition-colors duration-200 ${isActive ? 'text-accent-teal' : 'text-gray-400 group-hover:text-white'}`}
                      />
                    )}
                  </div>

                  {/* Label */}
                  <span className={`
                    flex-1 text-sm font-medium whitespace-nowrap
                    transition-all duration-300
                    ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}
                  `}>
                    {tab.label}
                  </span>

                  {/* Expand Arrow for SubTabs */}
                  {hasSubTabs && (isExpanded || isSidebarPinned || isForMobile) && (
                    <ChevronRight
                      size={16}
                      className={`
                        transition-transform duration-200
                        ${isSubMenuExpanded ? 'rotate-90' : ''}
                        ${isActive ? 'text-accent-teal' : 'text-gray-500'}
                      `}
                    />
                  )}

                  {/* Tooltip when collapsed - desktop only */}
                  {!isExpanded && !isSidebarPinned && !isForMobile && (
                    <div className="
                      absolute left-full ml-3 px-3 py-2 bg-dark-card border border-dark-border
                      rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible
                      transition-all duration-200 whitespace-nowrap z-50
                    ">
                      <span className="text-sm font-medium text-white">{tab.label}</span>
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-dark-card border-l border-b border-dark-border rotate-45" />
                    </div>
                  )}
                </div>

                {/* SubTabs */}
                {hasSubTabs && (
                  <div className={`
                    overflow-hidden transition-all duration-300 ease-in-out
                    ${isSubMenuExpanded && (isExpanded || isSidebarPinned || isForMobile) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                  `}>
                    <div className="ml-6 mt-1 space-y-1 border-l border-dark-border/50 pl-3">
                      {tab.subTabs?.map((subTab: any) => {
                        const SubIconComponent = getIconComponent(subTab.icon)
                        const isSubActive = currentSidebarSubTab === subTab.label

                        return (
                          <div
                            key={subTab.label}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSubTabClick(tab, subTab)
                            }}
                            className={`
                              flex items-center gap-2 px-3 py-2.5 md:py-2 rounded-lg cursor-pointer
                              transition-all duration-200 min-h-[44px] md:min-h-0
                              ${isSubActive
                                ? 'bg-accent-teal/10 text-accent-teal'
                                : 'text-gray-500 hover:bg-dark-card-hover hover:text-gray-300 active:bg-dark-card-hover'
                              }
                            `}
                          >
                            {SubIconComponent && (
                              <SubIconComponent size={14} className={isSubActive ? 'text-accent-teal' : 'text-gray-500'} />
                            )}
                            <span className="text-xs font-medium">{subTab.label}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="border-t border-dark-border p-2 space-y-1">
        {/* Profile */}
        <div
          onClick={() => setShowProfileModal(true)}
          className="group relative flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 text-gray-400 hover:bg-dark-card-hover hover:text-white min-h-[44px] md:min-h-0"
        >
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full overflow-hidden border-2 border-dark-border group-hover:border-accent-teal/50 transition-all duration-200">
            {currentProfile.profile_photo ? (
              <img
                src={currentProfile.profile_photo}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {getInitials(currentProfile.name)}
                </span>
              </div>
            )}
          </div>
          <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
            Profile
          </span>
          {!isExpanded && !isSidebarPinned && !isForMobile && (
            <div className="absolute left-full ml-3 px-3 py-2 bg-dark-card border border-dark-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
              <span className="text-sm font-medium text-white">Profile</span>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-dark-card border-l border-b border-dark-border rotate-45" />
            </div>
          )}
        </div>

        {/* Team - For all roles */}
        {['admin', 'dev', 'owner', 'manager', 'staff', 'director'].includes(currentProfile.privileges || '') && (
          <div
            onClick={() => navigate('/team')}
            className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 min-h-[44px] md:min-h-0 ${
              location.pathname === '/team'
                ? 'bg-gradient-to-r from-accent-teal/20 to-accent-purple/10 text-accent-teal'
                : 'text-gray-400 hover:bg-dark-card-hover hover:text-white'
            }`}
          >
            {location.pathname === '/team' && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-teal rounded-r-full" />
            )}
            <div className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
              location.pathname === '/team' ? 'bg-accent-teal/20' : 'group-hover:bg-dark-border/50'
            }`}>
              <Users size={18} className={`transition-colors duration-200 ${
                location.pathname === '/team' ? 'text-accent-teal' : 'text-gray-400 group-hover:text-white'
              }`} />
            </div>
            <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
              Team
            </span>
            {!isExpanded && !isSidebarPinned && !isForMobile && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-dark-card border border-dark-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                <span className="text-sm font-medium text-white">Team</span>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-dark-card border-l border-b border-dark-border rotate-45" />
              </div>
            )}
          </div>
        )}

        {/* Bell / Notifications - Only for intake department */}
        {currentProfile.department === 'intake' && (
          <div
            onClick={handleBellClick}
            className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 min-h-[44px] md:min-h-0 ${
              showBellReminder.isVisible
                ? 'bg-gradient-to-r from-accent-teal/20 to-accent-purple/10 text-accent-teal'
                : 'text-gray-400 hover:bg-dark-card-hover hover:text-white'
            }`}
          >
            {showBellReminder.isVisible && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-teal rounded-r-full" />
            )}
            <div className={`relative flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
              showBellReminder.isVisible ? 'bg-accent-teal/20' : 'group-hover:bg-dark-border/50'
            }`}>
              <Bell size={18} className={`transition-colors duration-200 ${
                showBellReminder.isVisible ? 'text-accent-teal' : 'text-gray-400 group-hover:text-white'
              }`} />
              {hasYellowStripeReminders && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-dark-card" />
              )}
            </div>
            <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
              Notifications
            </span>
            {!isExpanded && !isSidebarPinned && !isForMobile && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-dark-card border border-dark-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                <span className="text-sm font-medium text-white">Notifications</span>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-dark-card border-l border-b border-dark-border rotate-45" />
              </div>
            )}
          </div>
        )}

        {/* Feedback */}
        <div
          onClick={() => navigate('/feedback')}
          className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 min-h-[44px] md:min-h-0 ${
            location.pathname === '/feedback'
              ? 'bg-gradient-to-r from-accent-teal/20 to-accent-purple/10 text-accent-teal'
              : 'text-gray-400 hover:bg-dark-card-hover hover:text-white'
          }`}
        >
          {location.pathname === '/feedback' && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-accent-teal rounded-r-full" />
          )}
          <div className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
            location.pathname === '/feedback' ? 'bg-accent-teal/20' : 'group-hover:bg-dark-border/50'
          }`}>
            <MessageSquare size={18} className={`transition-colors duration-200 ${
              location.pathname === '/feedback' ? 'text-accent-teal' : 'text-gray-400 group-hover:text-white'
            }`} />
          </div>
          <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
            Feedback
          </span>
          {!isExpanded && !isSidebarPinned && !isForMobile && (
            <div className="absolute left-full ml-3 px-3 py-2 bg-dark-card border border-dark-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
              <span className="text-sm font-medium text-white">Feedback</span>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-dark-card border-l border-b border-dark-border rotate-45" />
            </div>
          )}
        </div>

        {/* Logout */}
        <div
          onClick={() => navigate('/auth/logout')}
          className="group relative flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 text-gray-400 hover:bg-red-500/10 hover:text-red-400 min-h-[44px] md:min-h-0"
        >
          <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg group-hover:bg-red-500/10 transition-all duration-200">
            <LogOut size={18} className="text-gray-400 group-hover:text-red-400 transition-colors duration-200" />
          </div>
          <span className={`text-sm font-medium whitespace-nowrap transition-all duration-300 ${isExpanded || isSidebarPinned || isForMobile ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
            Logout
          </span>
          {!isExpanded && !isSidebarPinned && !isForMobile && (
            <div className="absolute left-full ml-3 px-3 py-2 bg-dark-card border border-dark-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
              <span className="text-sm font-medium text-white">Logout</span>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-dark-card border-l border-b border-dark-border rotate-45" />
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        onMouseEnter={() => {
          // Cancel any pending close timeout
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current)
            closeTimeoutRef.current = null
          }
          setIsExpanded(true)
          setSidebarPinned(true)
        }}
        onMouseLeave={() => {
          // Delay closing by 0.5 seconds
          closeTimeoutRef.current = setTimeout(() => {
            setIsExpanded(false)
            setSidebarPinned(false)
          }, 500)
        }}
        className={`
          hidden md:flex h-full flex-col bg-dark-card border-r border-dark-border
          transition-all duration-300 ease-in-out
          ${isExpanded || isSidebarPinned ? 'w-56' : 'w-16'}
        `}
        style={{ height: maxHeight }}
      >
        {renderSidebarContent(false)}
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobile && isMobileSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-overlay"
            onClick={closeMobileSidebar}
          />
          {/* Sidebar */}
          <div
            className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-dark-card border-r border-dark-border animate-slide-in-left"
            style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {renderSidebarContent(true)}
          </div>
        </div>
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </>
  )
}

export default SidebarComponent
